import React, { Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetUser } from '../../../store/UserAction';
import { useParams, Link, Route, Routes, Navigate } from 'react-router-dom'
import * as Tab from '../../../util/TabValue';
import RenderTabs from '../../misc/Tabs';
import UserViewInfo from './UserViewInfo';
import UserViewObjectLogs from './UserViewObjectLogs';
import UserViewTrackings from './UserViewTrackings';
import UserViewUserRelations from './UserViewUserRelations';
import UserViewActivities from './UserViewActivities';
import UserViewGroups from './UserViewGroups';
import UserViewSchoolUnits from './UserViewSchoolUnits';
import NotFound from '../components/NotFound';
import { CoreUser } from '../../../models/CoreUser';
import { RequestState } from '../../../models/RequestState';
import StatusCode from '../../../util/StatusCode';
import Loading from '../../misc/Loading';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store/store';
import DeleteDate from '../../misc/DeleteDate';
import * as Helper from '../../../util/Helper';



const UserView = () => {
    const { t } = useTranslation(['common', 'schoolAdministration']);

    const { userId } = useParams();
    const dispatch = useDispatch();
    const user = useSelector<RootState, RequestState<CoreUser>>(state => state.users.user);

    useEffect(() => {
        if (userId) {
            GetUser(userId)(dispatch);
        }
    }, [dispatch, userId]);

    const tabs = [
        Tab.Tabs.Information,
        Tab.Tabs.ObjectLogs,
        Tab.Tabs.UserSchoolUnits,
        Tab.Tabs.Activities,
        Tab.Tabs.Groups,
        Tab.Tabs.UserRelations,
        Tab.Tabs.Trackings,
    ];

    if (userId === undefined) {
        return;
    }

    const renderContent = (body: any) => (
        <div className="padding-40 container single">
            <h1><Link to="/user">{t('object.schoolAdmin.person_one', { ns: 'common' })}</Link> {displayName}</h1>
            {body}
        </div>
    )

    let displayName: string | undefined = '';

    switch (user.code) {
        case StatusCode.COMPLETE:
            displayName = user.data.displayName;
            return (renderContent(<>

                {user.data.removeDate && <DeleteDate message={t('deleteDate.person', { ns: 'schoolAdministration', date: Helper.formatDateOnly(user.data.removeDate) })} />}

                <RenderTabs
                    tabs={tabs}
                    id={userId}
                    url="user" />

                <Suspense fallback={<Loading />}>
                    <Routes>
                        <Route path={Tab.Tabs.Information.url} element={<UserViewInfo />} />
                        <Route path={Tab.Tabs.ObjectLogs.url} element={<UserViewObjectLogs />} />
                        <Route path={Tab.Tabs.UserSchoolUnits.url} element={<UserViewSchoolUnits />} />
                        <Route path={Tab.Tabs.Activities.url} element={<UserViewActivities />} />
                        <Route path={Tab.Tabs.Groups.url} element={<UserViewGroups />} />
                        <Route path={Tab.Tabs.UserRelations.url} element={<UserViewUserRelations />} />
                        <Route path={Tab.Tabs.Trackings.url} element={<UserViewTrackings />} />
                        <Route path="*" element={<Navigate replace={true} to={`/user/${userId}/${Tab.Tabs.Information.url}`} />} />
                    </Routes>
                </Suspense>
            </>));
        case StatusCode.ERROR:
            if (user.error.response?.status === 404 || user.error.response?.status === 400) {
                return (
                    <NotFound
                        header={t('object.schoolAdmin.person', { ns: 'common', count: 2 })}
                        translateKey={{ i18nKey: "notFound.person", ns: "schoolAdministration" }}
                        link="/user"
                    />
                );
            } else {
                return renderContent(<p>{user.error.response?.statusText}</p>);
            }
        default:
            return (renderContent(<Loading />));
    }
}

export default UserView;
